'use client';

import { useCallback, useEffect, useState } from 'react';
import { Lightbulb, LightbulbOff, Plus, X } from 'lucide-react';
import { useBlockModalRgbLedStore } from '@/stores/useBlockModalRgbLedStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import ColorPicker from '@/components/ColorPicker';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import type { ColorData } from '@/(routers)/boards/editor/block/definitions/fields/fieldColorSequence';

const DURATIONS = [
    { label: 'もっと短い', value: 200 },
    { label: '短い', value: 500 },
    { label: 'ふつうの長さ', value: 1000 },
    { label: '長い', value: 2000 },
    { label: 'もっと長い', value: 5000 },
];

function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export default function RgbLed() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalRgbLedStore();
    const [colors, setColors] = useState<ColorData[]>(
        (fieldRef?.getColors() ?? []).map((c) => ({ ...c, enabled: c.enabled ?? true })),
    );

    const updateColor = (index: number, updates: Partial<ColorData>) => {
        setColors((prev) => prev.map((c, i) => (i === index ? { ...c, ...updates } : c)));
    };

    const addColor = () => {
        setColors((prev) => [...prev, { r: 255, g: 0, b: 0, duration: 1000, enabled: true }]);
    };

    const deleteColor = (index: number) => {
        setColors((prev) => prev.filter((_, i) => i !== index));
    };

    const handleOk = useCallback(() => {
        fieldRef?.setColors(colors);
        closeModal();
    }, [colors, fieldRef, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    return (
        <div className="flex flex-col h-full bg-color-block-action-modal-bg rounded-2xl overflow-hidden">
            {/* ヘッダー */}
            <div className="flex items-center px-3 h-[50px] bg-color-block-action-modal-header flex-shrink-0">
                <span className="text-xl mr-2">
                    <ImageAdaptive src="/images/block/rgb_led.svg" baseWidth={40} />
                </span>
                <span className="text-color-white-base font-semibold text-sm">フルカラーLEDの色設定</span>
            </div>

            {/* カラーリスト */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 min-h-0">
                {colors.map((color, i) => (
                    <div
                        key={i}
                        className="flex items-center gap-2 bg-color-block-action-modal-row-bg rounded-lg px-3 py-2 flex-shrink-0"
                    >
                        <span className="text-color-block-action-modal-text-index text-xs w-4 text-center flex-shrink-0">
                            {i + 1}
                        </span>

                        {/* 光る/光らないトグル */}
                        <div className="flex rounded-md overflow-hidden flex-shrink-0">
                            <button
                                onClick={() => updateColor(i, { enabled: true })}
                                className={`px-2 py-1 ${
                                    color.enabled
                                        ? 'bg-color-block-action-modal-header text-color-white-base'
                                        : 'bg-color-block-action-modal-button-bg text-color-block-action-modal-text-muted'
                                }`}
                            >
                                <IconAdaptive Icon={Lightbulb} strokeWidth={2} baseSize={16} />
                            </button>
                            <button
                                onClick={() => updateColor(i, { enabled: false })}
                                className={`px-2 py-1 ${
                                    !color.enabled
                                        ? 'bg-color-block-action-modal-rest-active text-color-white-base'
                                        : 'bg-color-block-action-modal-button-bg text-color-block-action-modal-text-muted'
                                }`}
                            >
                                <IconAdaptive Icon={LightbulbOff} strokeWidth={2} baseSize={16} />
                            </button>
                        </div>

                        {/* pillトリガー + カラーピッカー */}
                        <ColorPicker
                            className={`flex-1 ${!color.enabled ? 'opacity-30 pointer-events-none' : ''}`}
                            value={{ r: color.r, g: color.g, b: color.b }}
                            onChange={({ r, g, b }) => updateColor(i, { r, g, b })}
                            trigger={
                                <div className="flex items-center gap-2 h-8 px-3 w-full rounded-full border border-white/20 hover:border-white/50 transition-colors bg-black/30 cursor-pointer">
                                    <div
                                        className="w-20 h-3.5 rounded-full flex-shrink-0 border border-white/20"
                                        style={{ backgroundColor: rgbToHex(color.r, color.g, color.b) }}
                                    />
                                    <span className="flex justify-center text-white text-xs font-mono w-16 flex-shrink-0">
                                        {rgbToHex(color.r, color.g, color.b)}
                                    </span>
                                    <span className="text-white/50 text-xs font-mono w-30 flex-shrink-0">
                                        RGB({color.r}, {color.g}, {color.b})
                                    </span>
                                </div>
                            }
                        />

                        {/* 点灯時間 */}
                        <select
                            value={color.duration}
                            onChange={(e) => updateColor(i, { duration: Number(e.target.value) })}
                            className="w-28 flex-shrink-0 bg-color-block-action-modal-button-bg text-color-white-base border-none rounded text-xs px-2 py-1"
                        >
                            {DURATIONS.map((d) => (
                                <option key={d.value} value={d.value}>
                                    {d.label}
                                </option>
                            ))}
                        </select>

                        {/* 削除ボタン */}
                        <button
                            onClick={() => deleteColor(i)}
                            className="text-color-block-action-modal-text-index hover:text-color-block-action-modal-delete-hover text-sm px-1 flex-shrink-0"
                        >
                            <IconAdaptive Icon={X} strokeWidth={2} baseSize={16} />
                        </button>
                    </div>
                ))}
            </div>

            {/* フッター */}
            <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-color-block-action-modal-border flex-shrink-0">
                <button
                    onClick={addColor}
                    className="flex items-center pl-4 pr-5 py-2 bg-color-block-action-modal-button-bg border border-dashed border-color-block-action-modal-text-index rounded-lg text-color-block-action-modal-text-index text-xs hover:border-color-block-action-modal-header hover:text-color-block-action-modal-header transition-colors animate-pulse"
                    style={{ borderColor: CATEGORIES.action.color, color: CATEGORIES.action.color }}
                >
                    <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={16} className="mr-1" />
                    色を追加
                </button>
                <div className="flex gap-2">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-color-block-action-modal-cancel text-color-block-action-modal-text-gray rounded-lg text-sm"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOk}
                        className="px-6 py-2 bg-color-block-action-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-action-modal-ok-hover transition-colors"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}
